import React, { useState } from "react";
import { useNotification } from "../../context/NotificationContext";
import { createUserDocument } from "../../services/AdminService";
import { sortedStates, sortedRegions } from "../../utils/StaticFormData";
import { IoPersonAddOutline } from "react-icons/io5";

interface CreateUserFormProps {
  onUserCreated: () => void;
}

export const CreateUserForm = ({ onUserCreated }: CreateUserFormProps) => {
  const { showNotification } = useNotification();
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    artistName: "",
    stateID: 0,
    regionID: 0,
  });

  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewUserData({ ...newUserData, [e.target.name]: e.target.value });
  };

  const handleUserStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewUserData({
      ...newUserData,
      stateID: parseInt(e.target.value),
      regionID: 0,
    });
  };

  const handleUserRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewUserData({ ...newUserData, regionID: parseInt(e.target.value) });
  };

  const generateUsername = (name: string) =>
    name.replace(/\s+/g, "").toLowerCase();

  const handleCreateUser = async () => {
    if (
      !newUserData.artistName ||
      !newUserData.stateID ||
      !newUserData.regionID
    ) {
      showNotification("Please fill in all fields (Name, State, Region)");
      return;
    }

    setIsCreatingUser(true);
    try {
      const generatedUserName = generateUsername(newUserData.artistName);
      await createUserDocument({
        artistName: newUserData.artistName,
        name: newUserData.artistName,
        userName: generatedUserName,
        stateID: newUserData.stateID,
        regionID: newUserData.regionID,
        userType: "Artist",
        agreedToTerms: false,
        fans: 0,
        profileClaimed: false,
        lastCheckedNotifications: new Date(),
      });

      showNotification(`User created! Username: ${generatedUserName}`);
      setNewUserData({ artistName: "", stateID: 0, regionID: 0 });
      onUserCreated();
    } catch (error) {
      console.error(error);
      showNotification("Failed to create user.");
    } finally {
      setIsCreatingUser(false);
    }
  };

  const availableUserRegions = sortedRegions.filter(
    (region) => region.stateID === newUserData.stateID,
  );

  return (
    <div className="admin-section" style={{ marginTop: 24 }}>
      <h3 className="section-header">Create New User</h3>
      <div className="form-grid-2">
        <div className="form-group">
          <label>Artist Name</label>
          <input
            name="artistName"
            className="admin-input"
            placeholder="e.g. The Killers"
            value={newUserData.artistName}
            onChange={handleUserFormChange}
          />
        </div>
        <div className="form-group">
          <label>State</label>
          <select
            className="admin-select"
            value={newUserData.stateID}
            onChange={handleUserStateChange}
          >
            <option value={0}>Select State</option>
            {sortedStates.map((s) => (
              <option key={s.stateID} value={s.stateID}>
                {s.stateName}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Region</label>
          <select
            className="admin-select"
            value={newUserData.regionID}
            onChange={handleUserRegionChange}
            disabled={!newUserData.stateID}
          >
            <option value={0}>Select Region</option>
            {availableUserRegions.map((r) => (
              <option key={r.regionID} value={r.regionID}>
                {r.regionName}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Generated Username (Preview)</label>
          <input
            className="admin-input read-only"
            value={generateUsername(newUserData.artistName)}
            readOnly
          />
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <button
          className="btn-primary btn-full"
          onClick={handleCreateUser}
          disabled={isCreatingUser}
        >
          {isCreatingUser ? (
            "Creating User..."
          ) : (
            <>
              <IoPersonAddOutline size={20} style={{ marginRight: 8 }} />
              Create New User
            </>
          )}
        </button>
      </div>
    </div>
  );
};
